// ---------------------------------------------------------------------------
// Public API catalog — the single source of truth for the consumer endpoints.
//
// A typed description of every public endpoint: its method, a path template
// (with `{version}` and any `{pathParam}` placeholders), the request params,
// and the response field shapes. Consumed by the dashboard Playground (request
// builder + info tables + code samples) AND the MCP server (tool definitions).
//
// Keep `getApiVersions` (Vite `import.meta.env`) in the dashboard — it is the
// only catalog helper that is environment-specific. Everything here is pure.
// ---------------------------------------------------------------------------

import type { PlanTier } from "./index";

export type ParamLocation = "query" | "path";
export type ParamType = "string" | "number" | "boolean";

export interface CatalogParam {
  name: string;
  in: ParamLocation;
  type: ParamType;
  required: boolean;
  description: string;
  /** Pre-filled example value, used to seed the form. */
  example: string;
}

export interface CatalogResponseField {
  name: string;
  type: string;
  description: string;
}

export interface CatalogEndpoint {
  id: string;
  label: string;
  method: "GET";
  /** Path template, e.g. `/{version}/rates/{base}`. */
  pathTemplate: string;
  /** Short one-liner shown under the endpoint picker. */
  summary: string;
  /**
   * True when the endpoint needs NO API key (mounted without apiKeyAuth on the
   * Worker — e.g. /countries, /ppp). Drives the composer ("no key required"),
   * makes code samples omit the Authorization header, and lets the MCP server
   * expose the tool without an authorized connection.
   */
  public?: boolean;
  /**
   * Minimum plan tier required to call this endpoint. Omitted = available on
   * every plan. When set (e.g. "growth"), the Playground keeps the endpoint
   * visible but shows an upgrade notice + disables Run for lower tiers, and the
   * Worker enforces it with a 403. See `planHasHistoryApi` in the shared index.
   */
  minPlanTier?: PlanTier;
  params: CatalogParam[];
  response: CatalogResponseField[];
}

export type EndpointId =
  | "convert"
  | "rates"
  | "ratesByBase"
  | "history"
  | "historyAnalysis"
  | "pppAnalysis"
  | "volatility"
  | "correlation"
  | "drawdown"
  | "safeHaven"
  | "meanReversion"
  | "countries"
  | "currencies"
  | "ai";

export const ENDPOINTS: CatalogEndpoint[] = [
  {
    id: "convert",
    label: "Convert",
    method: "GET",
    pathTemplate: "/{version}/convert",
    summary: "Convert an amount between currencies, optionally PPP-adjusted.",
    params: [
      {
        name: "from",
        in: "query",
        type: "string",
        required: true,
        description:
          "Source currency (ISO 4217), e.g. USD. For PPP (ppp=true) pair it with a country as CUR:COUNTRY, e.g. USD:USA.",
        example: "USD",
      },
      {
        name: "to",
        in: "query",
        type: "string",
        required: true,
        description:
          "One or more target currencies, comma-separated, e.g. EUR,INR (max 25). For PPP use CUR:COUNTRY pairs, e.g. EUR:DEU,INR:IND.",
        example: "EUR,INR",
      },
      {
        name: "amount",
        in: "query",
        type: "number",
        required: false,
        description: "Amount to convert. Optional, defaults to 1 (so each result equals the rate).",
        example: "100",
      },
      {
        name: "ppp",
        in: "query",
        type: "boolean",
        required: false,
        description:
          "When true, apply a PPP adjustment. Every currency must then carry a country code.",
        example: "false",
      },
      {
        name: "date",
        in: "query",
        type: "string",
        required: false,
        description:
          "Rate date in YYYY-MM-DD (UTC). Must not be in the future. Defaults to latest.",
        example: "",
      },
    ],
    response: [
      { name: "from", type: "string", description: "The source currency you requested." },
      {
        name: "fromCountry",
        type: "string",
        description: "Source country (alpha-3). Present only when PPP was requested.",
      },
      { name: "amount", type: "number", description: "The input amount, echoed back." },
      { name: "date", type: "string", description: "The rate date actually used (YYYY-MM-DD, UTC)." },
      { name: "results", type: "array", description: "One entry per requested target." },
      { name: "results[].to", type: "string", description: "The target currency." },
      {
        name: "results[].toCountry",
        type: "string",
        description: "Target country (alpha-3). Present only when PPP was requested.",
      },
      {
        name: "results[].result",
        type: "number",
        description: "Plain FX conversion, not PPP-adjusted.",
      },
      {
        name: "results[].rate",
        type: "number",
        description: "Effective FX rate applied (result / amount).",
      },
      {
        name: "results[].ppp",
        type: "object",
        description:
          "PPP detail. Present only when PPP requested; a union of the success shape or { error }.",
      },
      {
        name: "results[].ppp.fromFactor",
        type: "number",
        description: "Source country's PPP conversion factor (success shape only).",
      },
      {
        name: "results[].ppp.toFactor",
        type: "number",
        description: "Target country's PPP conversion factor (success shape only).",
      },
      {
        name: "results[].ppp.result",
        type: "number",
        description: "PPP-adjusted amount: result × (fromFactor / toFactor).",
      },
      {
        name: "results[].ppp.error",
        type: "string",
        description: "Set instead of the factors when this target's PPP couldn't be applied.",
      },
    ],
  },
  {
    id: "rates",
    label: "Rates",
    method: "GET",
    pathTemplate: "/{version}/rates",
    summary: "The full exchange-rate snapshot for a date (USD base).",
    params: [
      {
        name: "date",
        in: "query",
        type: "string",
        required: false,
        description: "YYYY-MM-DD. Defaults to the latest available date.",
        example: "",
      },
    ],
    response: [
      { name: "base", type: "string", description: "Base currency of the snapshot (USD)." },
      { name: "date", type: "string", description: "The rate date used (YYYY-MM-DD, UTC)." },
      {
        name: "rates",
        type: "object",
        description: "Map of currency code → units per 1 unit of the base.",
      },
    ],
  },
  {
    id: "ratesByBase",
    label: "Rates by base",
    method: "GET",
    pathTemplate: "/{version}/rates/{base}",
    summary: "The same snapshot re-expressed against any base currency.",
    params: [
      {
        name: "base",
        in: "path",
        type: "string",
        required: true,
        description: "Base currency (3-letter ISO 4217), e.g. EUR.",
        example: "EUR",
      },
      {
        name: "date",
        in: "query",
        type: "string",
        required: false,
        description: "YYYY-MM-DD. Defaults to the latest available date.",
        example: "",
      },
    ],
    response: [
      { name: "base", type: "string", description: "The base currency you requested." },
      { name: "date", type: "string", description: "The rate date used (YYYY-MM-DD, UTC)." },
      {
        name: "rates",
        type: "object",
        description: "Map of currency code → units per 1 unit of the base (base itself is 1).",
      },
    ],
  },
  {
    id: "history",
    label: "History",
    method: "GET",
    pathTemplate: "/{version}/history",
    summary: "A single currency's daily rate time series over a date range. Growth plan or higher.",
    minPlanTier: "growth",
    params: [
      {
        name: "currency",
        in: "query",
        type: "string",
        required: true,
        description: "The currency whose history to fetch (3-letter ISO 4217), e.g. INR. One currency per call.",
        example: "INR",
      },
      {
        name: "from",
        in: "query",
        type: "string",
        required: false,
        description: "Start date YYYY-MM-DD (UTC). Defaults to 7 days before `to`.",
        example: "",
      },
      {
        name: "to",
        in: "query",
        type: "string",
        required: false,
        description: "End date YYYY-MM-DD (UTC). Defaults to today; a future date clamps to today.",
        example: "",
      },
      {
        name: "base",
        in: "query",
        type: "string",
        required: false,
        description: "Base currency the rates are expressed against (3-letter ISO 4217). Defaults to USD.",
        example: "USD",
      },
      {
        name: "interval",
        in: "query",
        type: "string",
        required: false,
        description:
          "Aggregation: daily (default), weekly, monthly, or yearly. Coarser intervals average the daily rates per bucket (weekly key = the week's Monday, monthly = YYYY-MM, yearly = YYYY).",
        example: "daily",
      },
    ],
    response: [
      { name: "currency", type: "string", description: "The currency you requested." },
      { name: "base", type: "string", description: "The base the rates are expressed against." },
      { name: "interval", type: "string", description: "The aggregation interval applied (daily/weekly/monthly/yearly)." },
      { name: "from", type: "string", description: "Inclusive start of the returned window (YYYY-MM-DD, UTC)." },
      { name: "to", type: "string", description: "Inclusive end of the returned window (YYYY-MM-DD, UTC)." },
      {
        name: "rates",
        type: "object",
        description:
          "Map of bucket → rate (units of `currency` per 1 `base`). Daily keys are dates; coarser intervals key by period and average the daily rates.",
      },
    ],
  },
  {
    id: "historyAnalysis",
    label: "History analysis",
    method: "GET",
    pathTemplate: "/{version}/history/analysis",
    summary:
      "Trends, comparisons & 'movers': % change, strength, and min/max/avg over a window. Growth plan or higher.",
    minPlanTier: "growth",
    params: [
      {
        name: "base",
        in: "query",
        type: "string",
        required: false,
        description: "Currency everything is measured against (3-letter ISO 4217). Defaults to USD.",
        example: "USD",
      },
      {
        name: "currencies",
        in: "query",
        type: "string",
        required: false,
        description:
          "Comma-separated currencies to compare, e.g. GBP,EUR,AED. OMIT to rank ALL currencies vs base ('movers' mode).",
        example: "GBP,EUR,AED",
      },
      {
        name: "from",
        in: "query",
        type: "string",
        required: false,
        description: "Window start YYYY-MM-DD (UTC). Defaults to 20 years (2 decades) before `to`. See `period` for a shorthand.",
        example: "",
      },
      {
        name: "to",
        in: "query",
        type: "string",
        required: false,
        description: "Window end YYYY-MM-DD (UTC). Defaults to today; a future date clamps to today.",
        example: "",
      },
      {
        name: "period",
        in: "query",
        type: "string",
        required: false,
        description: "Relative window shorthand for `from`: e.g. 10y, 6m, 30d, ytd, max. Ignored if `from` is given.",
        example: "10y",
      },
      {
        name: "sort",
        in: "query",
        type: "string",
        required: false,
        description: "Movers ranking: strengthened (default) or weakened.",
        example: "strengthened",
      },
      {
        name: "assetClass",
        in: "query",
        type: "string",
        required: false,
        description:
          "Movers universe: fiat (default, the liquid tradeable currencies), metals (XAU/XAG/XPT/XPD), or all (every currency incl. illiquid/frontier). fiat excludes metals/synthetics + illiquid frontier codes; redenomination artifacts are always dropped.",
        example: "fiat",
      },
      {
        name: "limit",
        in: "query",
        type: "number",
        required: false,
        description: "Movers: how many top results to return (default 10, max 200).",
        example: "10",
      },
      {
        name: "interval",
        in: "query",
        type: "string",
        required: false,
        description: "Attach a trend series per named currency: weekly, monthly, or yearly (compare mode only).",
        example: "yearly",
      },
      {
        name: "stats",
        in: "query",
        type: "boolean",
        required: false,
        description: "Include min/max/avg over the window per currency. Defaults to true; pass false to skip.",
        example: "true",
      },
    ],
    response: [
      { name: "base", type: "string", description: "Currency everything is measured against." },
      { name: "from", type: "string", description: "Window start used (YYYY-MM-DD, UTC)." },
      { name: "to", type: "string", description: "Window end used (YYYY-MM-DD, UTC)." },
      { name: "mode", type: "string", description: "`compare` (named currencies) or `movers` (ranked all)." },
      { name: "sort", type: "string", description: "Ranking direction (movers mode only)." },
      { name: "assetClass", type: "string", description: "Asset class the movers ranking was filtered to (movers mode only)." },
      { name: "stats", type: "boolean", description: "Whether min/max/avg were computed." },
      { name: "results", type: "array", description: "Per-currency analysis (ranked + limited in movers mode)." },
      { name: "results[].currency", type: "string", description: "The currency." },
      { name: "results[].startRate", type: "number", description: "Rate at window start (units per 1 base); null if no data then." },
      { name: "results[].endRate", type: "number", description: "Rate at window end; null if no data." },
      { name: "results[].changePct", type: "number", description: "% change in the quoted rate over the window." },
      { name: "results[].strengthenedPct", type: "number", description: "% the currency appreciated vs base (positive = stronger)." },
      { name: "results[].stats", type: "object", description: "{ min, max, avg } over the window (when stats=true)." },
      { name: "results[].series", type: "object", description: "period → avg rate trend (when interval set, compare mode)." },
    ],
  },
  {
    id: "pppAnalysis",
    label: "PPP analysis",
    method: "GET",
    pathTemplate: "/{version}/ppp/analysis",
    summary:
      "PPP over time: how a country's purchasing-power-parity factor changed, comparing countries, or 'movers'. Growth plan or higher.",
    minPlanTier: "growth",
    params: [
      {
        name: "countries",
        in: "query",
        type: "string",
        required: false,
        description:
          "Comma-separated ISO 3166-1 alpha-3 country codes to compare, e.g. IND,DEU,USA. OMIT to rank ALL countries by PPP-factor change ('movers' mode).",
        example: "IND,DEU,USA",
      },
      {
        name: "from",
        in: "query",
        type: "string",
        required: false,
        description: "Window start YEAR (e.g. 2015). Defaults to 10 years before `to`. See `period` for a shorthand.",
        example: "",
      },
      {
        name: "to",
        in: "query",
        type: "string",
        required: false,
        description: "Window end YEAR (e.g. 2024). Defaults to the current year.",
        example: "",
      },
      {
        name: "period",
        in: "query",
        type: "string",
        required: false,
        description: "Relative window shorthand for `from`: e.g. 10y, 20y, max. Ignored if `from` is given.",
        example: "10y",
      },
      {
        name: "sort",
        in: "query",
        type: "string",
        required: false,
        description: "Movers ranking: increased (PPP factor rose most, default) or decreased.",
        example: "increased",
      },
      {
        name: "limit",
        in: "query",
        type: "number",
        required: false,
        description: "Movers: how many top results to return (default 10, max 200).",
        example: "10",
      },
      {
        name: "stats",
        in: "query",
        type: "boolean",
        required: false,
        description: "Include min/max/avg of the PPP factor over the window per country. Defaults to true; pass false to skip.",
        example: "true",
      },
    ],
    response: [
      { name: "from", type: "number", description: "Window start year used." },
      { name: "to", type: "number", description: "Window end year used." },
      { name: "mode", type: "string", description: "`compare` (named countries) or `movers` (ranked all)." },
      { name: "sort", type: "string", description: "Ranking direction (movers mode only)." },
      { name: "stats", type: "boolean", description: "Whether min/max/avg were computed." },
      { name: "results", type: "array", description: "Per-country PPP analysis (ranked + limited in movers mode)." },
      { name: "results[].country", type: "string", description: "ISO alpha-3 country code." },
      { name: "results[].startYear", type: "number", description: "Year used for the window start (earliest available ≥ from)." },
      { name: "results[].endYear", type: "number", description: "Year used for the window end (latest available ≤ to)." },
      { name: "results[].startFactor", type: "number", description: "PPP factor at startYear (local units per international $); null if no data." },
      { name: "results[].endFactor", type: "number", description: "PPP factor at endYear; null if no data." },
      { name: "results[].changePct", type: "number", description: "% change in the PPP factor over the window (positive = factor rose)." },
      { name: "results[].projected", type: "boolean", description: "True when the end value is an IMF projection, not actual." },
      { name: "results[].stats", type: "object", description: "{ min, max, avg } over the window (when stats=true)." },
      { name: "results[].series", type: "object", description: "year → factor trend (compare mode)." },
    ],
  },
  {
    id: "volatility",
    label: "Volatility",
    method: "GET",
    pathTemplate: "/{version}/volatility",
    summary:
      "Annualized volatility of a currency vs a base, or a stability ranking of the liquid currencies. Growth plan or higher.",
    minPlanTier: "growth",
    params: [
      {
        name: "currency",
        in: "query",
        type: "string",
        required: false,
        description:
          "Currency to measure (3-letter ISO 4217), or a comma-separated list to compare. OMIT to RANK the liquid-currency set by stability.",
        example: "EUR",
      },
      {
        name: "base",
        in: "query",
        type: "string",
        required: false,
        description: "Base currency to measure against (3-letter ISO 4217). Defaults to USD.",
        example: "USD",
      },
      {
        name: "from",
        in: "query",
        type: "string",
        required: false,
        description: "Window start YYYY-MM-DD (UTC). Defaults to 365 days before `to`.",
        example: "",
      },
      {
        name: "to",
        in: "query",
        type: "string",
        required: false,
        description: "Window end YYYY-MM-DD (UTC). Defaults to today; a future date clamps to today.",
        example: "",
      },
      {
        name: "sort",
        in: "query",
        type: "string",
        required: false,
        description: "Ranking direction: stable (least volatile first, default) or volatile.",
        example: "stable",
      },
      {
        name: "universe",
        in: "query",
        type: "string",
        required: false,
        description: "Ranking universe: liquid (default, broad set) or majors (USD/EUR/GBP/JPY/CHF/CAD/AUD/NZD). Use majors for 'most volatile major pair'.",
        example: "liquid",
      },
      {
        name: "limit",
        in: "query",
        type: "number",
        required: false,
        description: "Ranking: how many to return (default 10, max 50).",
        example: "10",
      },
    ],
    response: [
      { name: "base", type: "string", description: "Base currency volatility is measured against." },
      { name: "from", type: "string", description: "Window start used (YYYY-MM-DD, UTC)." },
      { name: "to", type: "string", description: "Window end used (YYYY-MM-DD, UTC)." },
      { name: "mode", type: "string", description: "`compare` (named currencies) or `ranking` (liquid set)." },
      { name: "sort", type: "string", description: "Ranking direction (ranking mode only)." },
      { name: "basis", type: "string", description: "How volatility is annualized (`annualized-daily`)." },
      { name: "results", type: "array", description: "Per-currency volatility." },
      { name: "results[].currency", type: "string", description: "The currency." },
      { name: "results[].volatility", type: "number", description: "Annualized volatility in percent; null when too little data." },
      { name: "results[].observations", type: "number", description: "Number of daily returns the figure used." },
    ],
  },
  {
    id: "correlation",
    label: "Correlation",
    method: "GET",
    pathTemplate: "/{version}/correlation",
    summary:
      "Correlation of each currency's daily returns with a base currency's, over a window. Growth plan or higher.",
    minPlanTier: "growth",
    params: [
      {
        name: "base",
        in: "query",
        type: "string",
        required: false,
        description: "Reference currency to correlate against (3-letter ISO 4217). Defaults to USD (a non-USD base is more meaningful).",
        example: "EUR",
      },
      {
        name: "currencies",
        in: "query",
        type: "string",
        required: true,
        description: "Comma-separated currencies to correlate with `base`, e.g. GBP,CHF,SEK.",
        example: "GBP,CHF,SEK",
      },
      {
        name: "from",
        in: "query",
        type: "string",
        required: false,
        description: "Window start YYYY-MM-DD (UTC). Defaults to 365 days before `to`.",
        example: "",
      },
      {
        name: "to",
        in: "query",
        type: "string",
        required: false,
        description: "Window end YYYY-MM-DD (UTC). Defaults to today.",
        example: "",
      },
    ],
    response: [
      { name: "base", type: "string", description: "Reference currency." },
      { name: "from", type: "string", description: "Window start used (YYYY-MM-DD, UTC)." },
      { name: "to", type: "string", description: "Window end used (YYYY-MM-DD, UTC)." },
      { name: "results", type: "array", description: "Per-currency correlation with the base." },
      { name: "results[].currency", type: "string", description: "The currency." },
      { name: "results[].correlation", type: "number", description: "Pearson correlation of daily returns vs base (−1..1); null if undefined." },
      { name: "results[].observations", type: "number", description: "Overlapping daily returns used." },
    ],
  },
  {
    id: "drawdown",
    label: "Drawdown",
    method: "GET",
    pathTemplate: "/{version}/drawdown",
    summary:
      "Maximum peak-to-trough decline vs a base (with peak/trough/recovery), or a drawdown ranking. Growth plan or higher.",
    minPlanTier: "growth",
    params: [
      {
        name: "currency",
        in: "query",
        type: "string",
        required: false,
        description: "Currency to measure (3-letter ISO 4217), or a comma-separated list. OMIT to RANK the liquid set by drawdown.",
        example: "EUR",
      },
      {
        name: "base",
        in: "query",
        type: "string",
        required: false,
        description: "Base currency to measure against (3-letter ISO 4217). Defaults to USD.",
        example: "USD",
      },
      {
        name: "from",
        in: "query",
        type: "string",
        required: false,
        description: "Window start YYYY-MM-DD (UTC). Defaults to 365 days before `to`.",
        example: "",
      },
      {
        name: "to",
        in: "query",
        type: "string",
        required: false,
        description: "Window end YYYY-MM-DD (UTC). Defaults to today.",
        example: "",
      },
      {
        name: "sort",
        in: "query",
        type: "string",
        required: false,
        description: "Ranking direction: deepest (largest decline first, default) or recovery (fastest recovery first).",
        example: "deepest",
      },
      {
        name: "universe",
        in: "query",
        type: "string",
        required: false,
        description: "Ranking universe: liquid (default) or majors.",
        example: "liquid",
      },
      {
        name: "limit",
        in: "query",
        type: "number",
        required: false,
        description: "Ranking: how many to return (default 10, max 50).",
        example: "10",
      },
    ],
    response: [
      { name: "base", type: "string", description: "Base currency the decline is measured against." },
      { name: "from", type: "string", description: "Window start used (YYYY-MM-DD, UTC)." },
      { name: "to", type: "string", description: "Window end used (YYYY-MM-DD, UTC)." },
      { name: "mode", type: "string", description: "`compare` (named currencies) or `ranking`." },
      { name: "results", type: "array", description: "Per-currency maximum drawdown." },
      { name: "results[].currency", type: "string", description: "The currency." },
      { name: "results[].maxDrawdownPct", type: "number", description: "Worst peak-to-trough move in the quoted rate, signed percent; null if none." },
      { name: "results[].peakDate", type: "string", description: "Date of the peak the worst drawdown fell from." },
      { name: "results[].troughDate", type: "string", description: "Date of the trough." },
      { name: "results[].recoveryDate", type: "string", description: "First date the rate recovered to the peak, or null if not by `to`." },
      { name: "results[].recoveryDays", type: "number", description: "Calendar days from trough to recovery; null if not recovered." },
    ],
  },
  {
    id: "safeHaven",
    label: "Safe haven",
    method: "GET",
    pathTemplate: "/{version}/safe-haven",
    summary:
      "Ranks currencies by a composite safe-haven score (low volatility + shallow drawdown + 2008-crisis resilience). Growth plan or higher.",
    minPlanTier: "growth",
    params: [
      {
        name: "currencies",
        in: "query",
        type: "string",
        required: false,
        description: "Comma-separated currencies to score, e.g. CHF,JPY. OMIT to rank the safe-haven pool (includes gold).",
        example: "CHF,JPY",
      },
      {
        name: "base",
        in: "query",
        type: "string",
        required: false,
        description: "Base currency to measure against (3-letter ISO 4217). Defaults to USD.",
        example: "USD",
      },
      {
        name: "from",
        in: "query",
        type: "string",
        required: false,
        description:
          "Window start YYYY-MM-DD (UTC). Defaults to 2007-01-01 (spans the 2008 crisis + COVID), or to 20 years before `to` when that is later.",
        example: "",
      },
      {
        name: "to",
        in: "query",
        type: "string",
        required: false,
        description: "Window end YYYY-MM-DD (UTC). Defaults to today.",
        example: "",
      },
      {
        name: "limit",
        in: "query",
        type: "number",
        required: false,
        description: "How many to return (default 10, max 50).",
        example: "10",
      },
    ],
    response: [
      { name: "base", type: "string", description: "Base currency the metrics are measured against." },
      { name: "from", type: "string", description: "Window start used (YYYY-MM-DD, UTC)." },
      { name: "to", type: "string", description: "Window end used (YYYY-MM-DD, UTC)." },
      { name: "crisis", type: "object", description: "The reference crisis window: { label, from, to }." },
      { name: "results", type: "array", description: "Currencies ranked by safe-haven score (highest first)." },
      { name: "results[].currency", type: "string", description: "The currency." },
      { name: "results[].score", type: "number", description: "Composite safe-haven score 0-100 (higher = safer); null if no data." },
      { name: "results[].volatility", type: "number", description: "Annualized volatility (%) over the window." },
      { name: "results[].maxDrawdownPct", type: "number", description: "Worst peak-to-trough decline (signed %)." },
      { name: "results[].crisisReturnPct", type: "number", description: "% it appreciated vs base during the reference crisis (positive = held value)." },
    ],
  },
  {
    id: "meanReversion",
    label: "Mean reversion",
    method: "GET",
    pathTemplate: "/{version}/mean-reversion",
    summary:
      "Ranks currencies by how strongly they revert to their mean (crossing frequency + reversion half-life). Growth plan or higher.",
    minPlanTier: "growth",
    params: [
      {
        name: "currencies",
        in: "query",
        type: "string",
        required: false,
        description: "Comma-separated currencies to score, e.g. EUR,GBP. OMIT to rank the liquid set.",
        example: "EUR,GBP",
      },
      {
        name: "base",
        in: "query",
        type: "string",
        required: false,
        description: "Base currency to measure against (3-letter ISO 4217). Defaults to USD.",
        example: "USD",
      },
      {
        name: "from",
        in: "query",
        type: "string",
        required: false,
        description: "Window start YYYY-MM-DD (UTC). Defaults to ~2 years before `to`.",
        example: "",
      },
      {
        name: "to",
        in: "query",
        type: "string",
        required: false,
        description: "Window end YYYY-MM-DD (UTC). Defaults to today.",
        example: "",
      },
      {
        name: "limit",
        in: "query",
        type: "number",
        required: false,
        description: "How many to return (default 10, max 50).",
        example: "10",
      },
    ],
    response: [
      { name: "base", type: "string", description: "Base currency the mean is measured against." },
      { name: "from", type: "string", description: "Window start used (YYYY-MM-DD, UTC)." },
      { name: "to", type: "string", description: "Window end used (YYYY-MM-DD, UTC)." },
      { name: "results", type: "array", description: "Currencies ranked by mean-reversion score (highest first)." },
      { name: "results[].currency", type: "string", description: "The currency." },
      { name: "results[].score", type: "number", description: "Composite 0-100 (higher = more mean-reverting); null if no data." },
      { name: "results[].deviationPct", type: "number", description: "Current deviation from the window mean (signed %)." },
      { name: "results[].reversionFrequency", type: "number", description: "Mean crossings per observation (0..1)." },
      { name: "results[].halfLifeDays", type: "number", description: "Estimated half-life of a deviation in days; null when non-reverting." },
    ],
  },
  {
    id: "countries",
    label: "Countries",
    method: "GET",
    pathTemplate: "/{version}/countries",
    summary: "The supported countries and their official currencies. Public, no key needed.",
    public: true,
    params: [],
    response: [
      { name: "[].code", type: "string", description: "Country code (ISO/IMF alpha-3), e.g. USA." },
      { name: "[].name", type: "string", description: "Human-readable country name." },
      {
        name: "[].currencies",
        type: "string[]",
        description: "The country's official currencies (ISO 4217), e.g. [\"INR\"].",
      },
    ],
  },
  {
    id: "currencies",
    label: "Currencies",
    method: "GET",
    pathTemplate: "/{version}/currencies",
    summary:
      "The full list of supported ISO 4217 currencies. Use it to resolve a name or symbol to its code, or to check whether a currency is supported (so a missing one fails clearly, not silently). Public, no key needed.",
    public: true,
    params: [],
    response: [
      { name: "[].code", type: "string", description: "ISO 4217 alpha code, e.g. USD, INR." },
      { name: "[].numeric", type: "string", description: "ISO 4217 numeric code, zero-padded, e.g. 840." },
      { name: "[].name", type: "string", description: "Human-readable currency name." },
      { name: "[].symbol", type: "string", description: "Currency symbol, e.g. $, ₹." },
    ],
  },
  {
    id: "ai",
    label: "AI",
    method: "GET",
    pathTemplate: "/{version}/ai",
    summary: "Ask a natural-language currency question; the model answers using live rates.",
    params: [
      {
        name: "q",
        in: "query",
        type: "string",
        required: true,
        description:
          "A natural-language question, e.g. \"Convert 50 euro to indian rupees\" or \"What's the USD to EUR rate today?\".",
        example: "Convert 50 euro to indian rupees",
      },
    ],
    response: [
      { name: "query", type: "string", description: "Your original question, echoed back." },
      { name: "answer", type: "string", description: "A natural-language answer to the question." },
      {
        name: "data",
        type: "array",
        description:
          "The structured result object(s) returned by the tool that answered. Shape depends on the question: a conversion yields a /convert result, a rate lookup a /rates map, a trend/comparison (Growth+) a /history/analysis object. Empty when no lookup was needed.",
      },
    ],
  },
];

export function getEndpoint(id: EndpointId): CatalogEndpoint {
  const found = ENDPOINTS.find((e) => e.id === id);
  if (!found) throw new Error(`Unknown endpoint: ${id}`);
  return found;
}

/** The latest version is the last entry (list is assumed ascending). */
export function latestVersion(versions: string[]): string {
  return versions[versions.length - 1] ?? "v1";
}

/**
 * Build the API path (path + query string) for an endpoint, version, and the
 * map of filled param values. Path params are substituted; query params are
 * appended; empty optional params are omitted. Booleans only emit when true.
 */
export function buildPath(
  endpoint: CatalogEndpoint,
  version: string,
  values: Record<string, string>,
): string {
  let path = endpoint.pathTemplate.replace("{version}", version);

  // Substitute path params.
  for (const p of endpoint.params) {
    if (p.in !== "path") continue;
    const raw = (values[p.name] ?? "").trim() || p.example || "";
    path = path.replace(`{${p.name}}`, encodeURIComponent(raw));
  }

  // Append query params.
  const q = new URLSearchParams();
  for (const p of endpoint.params) {
    if (p.in !== "query") continue;
    const raw = (values[p.name] ?? "").trim();
    if (p.type === "boolean") {
      if (raw === "true") q.set(p.name, "true");
      continue;
    }
    if (raw) q.set(p.name, raw);
  }
  const qs = q.toString();
  return qs ? `${path}?${qs}` : path;
}
